// 공정 체크 공용 계산 유틸 — 읽기 전용 순수 함수 (Firestore 접근 없음)
// 정본: docs/기획서_QR공정체크_2026-08.md §4-1~4-3, §4-7
//
// 설계 원칙 (지키지 않으면 데이터가 두 군데로 갈라진다)
//  1) 자동 판정분(스캔·밀링)은 checks 맵에 복제 저장하지 않는다. 기존 필드
//     (processStage / designDone / stageHistory)에서 "파생"해 표시만 한다.
//  2) 사람이 누르는 4개(implantMill·modelPrint·jigPrint·packing)만 checks에 저장한다.
//  3) 사람이 자동 항목을 되돌리면 그때만 checks.scan / checks.mill 이 생기고,
//     revertedFromAuto:true 로 "되돌림" 흔적을 남긴다(파생값보다 우선).
//  4) 입력은 workOrders(웹 의뢰서) · parsedCases(자동 수집) 두 스키마 모두 허용한다.
//
// 이 파일은 예외를 던지지 않는다. 값이 이상하면 null/false 로 떨어뜨린다.
// 1.3.0(2026-10-12): 구 utils/clinicStage.js 대신 공통 정본 사본을 직접 쓴다(stageColOf·isClosedStage 결과 동일)
import { stageColOf, isClosedStage, STALL_STAGES, STALL_VISIT_SLACK_DAYS } from './shared/clinic-stage';

// ── 체크 6종 (순서 = 실제 작업 공정 순, 고정) ──
export const CHECK_DEFS = [
    { key: 'scan', label: '스캔', autoCapable: true },
    { key: 'mill', label: '밀링', autoCapable: true },
    { key: 'implantMill', label: '임플란트 밀링', autoCapable: false },
    { key: 'modelPrint', label: 'Model printing', autoCapable: false },
    { key: 'jigPrint', label: 'Jig printing', autoCapable: false },
    { key: 'packing', label: '포장 완료', autoCapable: false },
];

export const CHECK_KEYS = CHECK_DEFS.map(d => d.key);
export const CHECK_LABELS = CHECK_DEFS.reduce((m, d) => { m[d.key] = d.label; return m; }, {});

// ── 단계 순서 (processStage) — 에이전트·rules와 공유하는 원본 모델, 변경 금지 ──
const STAGE_ORDER = { received: 0, scanning: 1, designing: 2, milling: 3, shipping: 4, done: 5 };
const stageRank = (stage) => {
    const r = STAGE_ORDER[String(stage || '')];
    return typeof r === 'number' ? r : -1;
};

// ── 날짜 유틸 (Firestore Timestamp / Date / 문자열 / 숫자 혼재 방어) ──
export const toDate = (v) => {
    if (v === null || v === undefined || v === '') return null;
    try {
        if (typeof v.toDate === 'function') {                 // Firestore Timestamp
            const d = v.toDate();
            return d instanceof Date && !isNaN(d.getTime()) ? d : null;
        }
        if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
        if (typeof v === 'object' && typeof v.seconds === 'number') {   // 직렬화된 Timestamp
            const d = new Date(v.seconds * 1000);
            return isNaN(d.getTime()) ? null : d;
        }
        if (typeof v === 'string' || typeof v === 'number') {
            const d = new Date(v);
            return isNaN(d.getTime()) ? null : d;
        }
    } catch (e) {
        // 조용한 실패 금지 — 값 자체는 남기지 않는다(환자정보가 섞일 수 있음)
        console.error('caseChecks.toDate: 날짜 변환 실패', e?.message || e);
    }
    return null;
};

const pad2 = (n) => String(n).padStart(2, '0');

// 체크 시각 표시용 (예: 08-09 15:42)
export const formatCheckTime = (v) => {
    const d = toDate(v);
    if (!d) return '';
    return `${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const startOfDay = (d) => { const x = new Date(d.getTime()); x.setHours(0, 0, 0, 0); return x; };

// 마감 D-day 계산 — { days, text, state } / state: 'late'|'today'|'soon'|'normal'
export const formatDday = (deadline, basis) => {
    const d = toDate(deadline);
    if (!d) return null;
    const days = Math.round((startOfDay(d).getTime() - startOfDay(new Date()).getTime()) / 86400000);
    const head = basis === 'visit' ? '내원' : '납기';
    if (days < 0) return { days, state: 'late', text: `${head} 지남 (D+${-days})` };
    if (days === 0) return { days, state: 'today', text: `${head} 오늘` };
    return { days, state: days === 1 ? 'soon' : 'normal', text: `${head} D-${days}` };
};

// ── stageHistory에서 특정 단계의 시각 (가장 이른 기록 = 그 단계에 처음 들어간 때) ──
const stageEnteredAt = (entity, stage) => {
    const hist = Array.isArray(entity?.stageHistory) ? entity.stageHistory : [];
    let best = null;
    for (const h of hist) {
        if (!h || h.stage !== stage) continue;
        const d = toDate(h.at);
        if (!d) continue;
        if (!best || d.getTime() < best.getTime()) best = d;
    }
    return best;
};

// ── checks 맵 읽기 (구필드 폴백 포함, 절대 삭제하지 않음) ──
// 승계(기획서 Q2): millChecked → 밀링, printChecked → Model printing
const LEGACY_FIELD = { mill: 'millChecked', modelPrint: 'printChecked' };
// 저장 키 흔들림 방어: checks.mill 이 정본, checks.milling 은 읽기만 허용(쓰지 않음)
const ALT_CHECK_KEY = { mill: 'milling', packing: 'packed' };

const readCheck = (entity, key) => {
    const checks = (entity && typeof entity.checks === 'object' && entity.checks) || {};
    let raw = checks[key];
    if (raw === undefined && ALT_CHECK_KEY[key]) raw = checks[ALT_CHECK_KEY[key]];
    if (raw && typeof raw === 'object') {
        return { done: raw.done === true, at: toDate(raw.at), by: raw.by || null, reverted: raw.revertedFromAuto === true, source: 'checks' };
    }
    if (raw === true) return { done: true, at: null, by: null, reverted: false, source: 'checks' };
    const legacy = LEGACY_FIELD[key];
    if (legacy && entity && entity[legacy] === true) {
        return { done: true, at: toDate(entity[legacy + 'At']), by: 'user', reverted: false, source: 'legacy' };
    }
    return null;
};

// ── 자동 판정 (복제 저장 없음 — 표시 계층에서만 파생) ──
const deriveAuto = (entity, key) => {
    const rank = stageRank(entity?.processStage);
    if (key === 'scan') {
        // 스캔: processStage 가 scanning 이상이면 완료로 본다
        if (rank >= STAGE_ORDER.scanning) {
            return { done: true, at: stageEnteredAt(entity, 'scanning') || toDate(entity?.stageUpdatedAt) };
        }
        return { done: false, at: null };
    }
    if (key === 'mill') {
        if (rank >= STAGE_ORDER.milling) {
            return { done: true, at: stageEnteredAt(entity, 'milling') || toDate(entity?.stageUpdatedAt) };
        }
        return { done: false, at: null };
    }
    return { done: false, at: null };
};

// ── 마감일 (내원일 우선 → 납기 폴백) ──
export const getDeadline = (entity) => {
    const visit = toDate(entity?.visitDate);
    if (visit) return { deadline: visit, basis: 'visit' };
    const due = toDate(entity?.deliveryDate) || toDate(entity?.dueDate)
        || toDate(entity?.completionDate) || toDate(entity?.deliveryDueDate);
    if (due) return { deadline: due, basis: 'due' };
    return { deadline: null, basis: null };
};

// ── 케이스별 적용 공정 (대표 확정 2026-08-09 전체회의) ──
// 스캔·밀링·포장 = 모든 케이스 공통 / implantMill·jigPrint = 임플란트 케이스만 /
// modelPrint = 구강스캔(디지털 인상) 케이스만 — 석고 모델이 이미 있으면 프린트가 필요 없다.
// 판정 신호가 없으면 modelPrint는 기본 포함(확정: "구분 불가 시 기본값+수동 가감").
// 사람이 이미 체크한 항목은 항상 적용으로 본다(수동 가감).
export function applicableKeysOf(entity) {
    const e = entity || {};
    const keys = new Set(['scan', 'mill', 'packing']);
    const tds = Array.isArray(e.teethDetails) ? e.teethDetails : [];
    const its = Array.isArray(e.items) ? e.items : [];
    const hasImplant =
        tds.some(d => d && (d.implant || d.implantType)) ||
        its.some(i => i && (i.implant || /implant|임플란트|abutment|어버트/i.test(String(i.prosthesis || '')))) ||
        (Array.isArray(e.implantGroups) && e.implantGroups.length > 0) ||
        (Array.isArray(e.indications) && e.indications.some(x => /implant|임플란트|abutment/i.test(String(x))));
    if (hasImplant) { keys.add('implantMill'); keys.add('jigPrint'); }
    const ant = String(e.antagonist || '');
    const plaster = /석고/.test(ant);
    const digital = /디지털/.test(ant) || !!e.linkedScan || !!e.scanId ||
        (Array.isArray(e.digitalFiles) && e.digitalFiles.length > 0);
    if (digital || !plaster) keys.add('modelPrint');   // 신호 없으면 기본 포함
    const checks = (e.checks && typeof e.checks === 'object') ? e.checks : {};
    CHECK_KEYS.forEach(k => {
        const c = checks[k] !== undefined ? checks[k] : (ALT_CHECK_KEY[k] ? checks[ALT_CHECK_KEY[k]] : undefined);
        if (c === true || (c && c.done === true)) keys.add(k);
    });
    return keys;
}

// ── 멈춤 판정 — 표시 4단계 기준 (2026-09-29 대표 확정 4단계: 접수 2일 · 확인 3일 · 디자인완료 3일 · 밀링완료 2일 기본값 — 일수는 대표 확인 예정) ──
// 표시 전용 파생 계산: 어디에도 저장하지 않는다. 기준표는 화면에 그대로 공개한다
// ("숨긴 기준은 믿지 않는다" — 회의록_2026-08-25). 저장 단계(processStage 5값)는 그대로 두고
// utils/shared/clinic-stage.stageColOf 로 4칸(intake/confirm/design/mill, 종결=null)에 접은 뒤 그 칸의 체류일을 잰다.
// ★ functions/index.js dailyStallDigest(서버 알림)에는 구 5단계 임계(2/2/3/2/3일)가 따로 복제되어 있다
//   (상태 복제 저장 금지 원칙이라 코드만 복제, P2 2026-08-25). 이번 개편은 웹 표시 계층만 — CF는 미변경(별도 정렬 필요).
// 1.3.0(2026-10-12 대표 "권고대로" 3단계): 기준표 정본은 shared/clinic-stage.js STALL_STAGES(서버 dailyStallDigest와 같은 값).
// 칸 키(intake/confirm/design/mill)·stallNotifiedAt 표식 키는 그대로, 이름만 3단계(의뢰서 확인 전·의뢰서 확인·디자인 완료).
export { STALL_STAGES, STALL_VISIT_SLACK_DAYS };

// 표시 칸(4단계)에 "마지막으로" 들어간 시각 — stageHistory를 칸 단위로 접어, 현재 칸과 다른 칸의
// 최신 기록 이후 첫 기록을 진입 시각으로 본다(되돌리기 후 재진입까지 반영해야 체류일이 맞다).
// designing 안에서 designDone만 바뀐 전이(확인→디자인완료)는 이력에 남지 않으므로 호출부가 designDoneAt·stageUpdatedAt로 보정한다.
// (이력의 'designing' 기록은 현재 designDone 값으로 접히므로 confirm/design 중 지금 칸 쪽으로만 잡힌다 — 밀링 이력은 'mill'로 그대로 접힌다.)
const colLatestEnteredAt = (entity, col) => {
    const hist = Array.isArray(entity?.stageHistory) ? entity.stageHistory : [];
    const rows = hist
        .map(h => ({ col: h && h.stage ? stageColOf(h.stage, entity?.designDone) : null, at: toDate(h?.at) }))
        .filter(x => x.col && x.at)
        .sort((a, b) => a.at.getTime() - b.at.getTime());
    let entered = null;
    for (const x of rows) {
        if (x.col === col) { if (!entered) entered = x.at; }
        else entered = null;   // 다른 칸 기록이 나오면 현재 칸 진입 시각을 다시 잰다
    }
    return entered;
};

/**
 * getStallInfo(entity, now?) — 현재 표시 단계(4칸)의 체류일·멈춤 여부를 계산한다. (순수 함수, 저장 금지)
 * entity: workOrders 또는 parsedCases 문서. processStage가 없으면 '의뢰서 접수' 대기로 본다.
 * 반환: { stage, label, daysInStage, threshold, stalled, tightVisit, enteredAt }
 *  - stage: 표시 칸 키(intake/confirm/design/mill). 저장값(processStage)이 아니다. 종결(shipping/done)은 null.
 *  - daysInStage: 달력일 기준 체류일 (오늘 들어옴=0). 진입 시각을 못 읽으면 null → stalled false
 *    (모르는 것을 멈춤으로 지어내지 않는다).
 *  - threshold: 기준표 값. 내원일(visitDate)까지 여유 3일 미만이면 전 단계 1일 (대표 확정 D6).
 *  - processStage가 done(전체 완료)이거나 모르는 값이면 stalled false 고정.
 */
export function getStallInfo(entity, now = new Date()) {
    const e = entity || {};
    const raw = e.processStage;
    const stage = raw ? (raw === 'done' ? null : stageColOf(raw, e.designDone)) : 'intake';
    const none = { stage, label: null, daysInStage: null, threshold: null, stalled: false, tightVisit: false, enteredAt: null };
    // 종결(shipping/done·status completed)은 판정 대상 아님 — 종결 규칙은 shared/clinic-stage.isClosedStage 한 곳 (2026-09-29 검토 P2-3)
    if (isClosedStage(e)) return { ...none, stage: null };
    const def = stage ? STALL_STAGES.find(s => s.stage === stage) : null;
    if (!def) return none;                        // done·모르는 저장값 — 멈춤 판정 대상 아님
    // 내원 여유 판정 (내원일 없으면 기본 임계)
    const visit = toDate(e.visitDate);
    let tightVisit = false;
    if (visit) {
        const slack = Math.round((startOfDay(visit).getTime() - startOfDay(now).getTime()) / 86400000);
        tightVisit = slack < STALL_VISIT_SLACK_DAYS;
    }
    const threshold = tightVisit ? 1 : def.days;
    // 칸 진입 시각: 현재 칸 최신 진입 이력 → stageUpdatedAt(공정 기록 있을 때만)
    //  → intake 한정 폴백: createdAt(의뢰서 도착 시각) → readAt
    let entered = raw ? colLatestEnteredAt(e, stage) : null;
    if (stage === 'design') {
        // 확인(디자인 시작)→디자인완료 전이는 이력에 남지 않는다 — 진입 시각은 designDoneAt만 인정한다.
        // designDoneAt이 없으면 null로 두어 멈춤으로 세지 않는다(모르는 것을 멈춤으로 지어내지 않는다).
        // stageUpdatedAt·이력 폴백 금지: 그 값은 '확인' 칸 진입 시각이라 디자인완료 체류일이 부풀어 오탐이 난다.
        // (에이전트 markDesignDone이 designDone만 쓰고 시각을 안 남기는 케이스가 있다 — 2026-09-29 검토 P1-1)
        entered = toDate(e.designDoneAt) || null;
    } else if (!entered && raw) {
        entered = toDate(e.stageUpdatedAt);
    }
    if (!entered && stage === 'intake') entered = toDate(e.createdAt) || toDate(e.readAt);
    if (!entered) return { ...none, label: def.label, threshold };
    const daysInStage = Math.max(0, Math.floor((startOfDay(now).getTime() - startOfDay(entered).getTime()) / 86400000));
    return { stage, label: def.label, daysInStage, threshold, stalled: daysInStage >= threshold, tightVisit, enteredAt: entered };
}

/**
 * deriveChecks(entity) — 체크 6종의 표시 상태를 계산한다. (순수 함수)
 * entity: workOrders 또는 parsedCases 문서(평범한 객체). 필드가 없어도 된다.
 * 진행률(progress)·다음 공정(nextStep)은 이 케이스에 "적용되는 공정"만으로 센다 (대표 확정 08-09).
 */
export function deriveChecks(entity) {
    const e = entity || {};
    const applicable = applicableKeysOf(e);
    const items = CHECK_DEFS.map(def => {
        const stored = readCheck(e, def.key);
        // 저장된 기록이라도 by==='auto'면 프로그램이 남긴 것 — 사람 체크로 보이면 안 된다
        // (2026-08-09 전체회의 P1-5: '자동' 배지·되돌림 확인창·revertedFromAuto가 전부 이 값에 달려 있다)
        const storedIsAuto = !!(stored && stored.by === 'auto');
        if (def.autoCapable) {
            const auto = deriveAuto(e, def.key);
            if (stored) {
                // 사람 기록이 자동 파생보다 우선 (되돌림 포함). 단, 자동 기록이면 자동으로 표시한다.
                return {
                    key: def.key, label: def.label,
                    done: stored.done,
                    at: stored.at || (stored.done ? auto.at : null),
                    by: stored.by || 'user',
                    auto: storedIsAuto && stored.done,
                    reverted: stored.reverted || (!stored.done && auto.done),
                };
            }
            return {
                key: def.key, label: def.label,
                done: auto.done, at: auto.at, by: auto.done ? 'auto' : null,
                auto: auto.done, reverted: false,
            };
        }
        if (stored) {
            // 자동 판정 대상이 아닌 항목도 프로그램이 기록했다면(by==='auto') 자동으로 표시
            return {
                key: def.key, label: def.label,
                done: stored.done, at: stored.at, by: stored.by || 'user',
                auto: storedIsAuto && stored.done, reverted: stored.reverted,
            };
        }
        return { key: def.key, label: def.label, done: false, at: null, by: null, auto: false, reverted: false };
    }).map(it => ({ ...it, applicable: applicable.has(it.key) }));

    // 진행률·다음 공정은 적용 공정만으로 (대표 확정 08-09 — 임플란트 아닌 케이스에 지그가 끼면 영영 6/6이 안 된다)
    const appItems = items.filter(i => i.applicable);
    const doneCount = appItems.filter(i => i.done).length;
    const next = appItems.find(i => !i.done) || null;
    const packingItem = items.find(i => i.key === 'packing');
    const { deadline, basis } = getDeadline(e);

    return {
        items,
        doneCount,
        total: appItems.length,
        progress: `${doneCount}/${appItems.length}`,
        nextStep: next ? next.key : null,
        nextLabel: next ? next.label : null,
        packed: !!(packingItem && packingItem.done),
        packedAt: packingItem && packingItem.done ? packingItem.at : null,
        deadline,
        deadlineBasis: basis,
    };
}

/**
 * buildCheckPatch(key, nextValue, entity)
 * Firestore updateDoc에 그대로 넣을 부분 갱신 객체를 만든다(쓰기는 하지 않는다).
 * - 점 표기(checks.packing)로 다른 체크를 덮어쓰지 않는다.
 * - workOrders는 rules hasOnly 화이트리스트 정합을 위해 processStage를 동봉한다
 *   (기존 ProductionBoard.toggleCheck와 같은 방식 — parsedCases는 불필요).
 */
export function buildCheckPatch(key, nextValue, entity) {
    if (!CHECK_KEYS.includes(key)) {
        console.error('caseChecks.buildCheckPatch: 허용되지 않은 체크 키', key);
        return null;
    }
    const e = entity || {};
    const derived = deriveChecks(e).items.find(i => i.key === key);
    const wasAuto = !!(derived && derived.auto);
    const patch = {
        [`checks.${key}`]: {
            done: nextValue === true,
            at: nextValue === true ? new Date().toISOString() : null,
            by: 'user',
            revertedFromAuto: !nextValue && wasAuto,
        },
    };
    // 수집 케이스(parsedCases)는 단계 동봉이 필요 없다
    const isParsed = e._isParsed === true || e._col === 'parsedCases';
    if (!isParsed && e.processStage) patch.processStage = e.processStage;
    return patch;
}

/**
 * mergeCheckEntities(...entities)
 * 같은 케이스가 workOrders와 parsedCases 두 문서로 나뉜 경우의 합집합 계산용.
 * 같은 키는 at이 최신인 쪽을 채택한다. (표시 전용 — 저장에는 쓰지 않는다)
 */
export function mergeCheckEntities(...entities) {
    const list = entities.filter(Boolean);
    if (list.length === 0) return deriveChecks({});
    const derivedList = list.map(deriveChecks);
    const items = CHECK_DEFS.map((def, idx) => {
        let picked = derivedList[0].items[idx];
        for (const d of derivedList.slice(1)) {
            const cand = d.items[idx];
            if (!picked.done && cand.done) { picked = cand; continue; }
            if (picked.done && cand.done) {
                const a = picked.at ? picked.at.getTime() : 0;
                const b = cand.at ? cand.at.getTime() : 0;
                if (b > a) picked = cand;
            }
        }
        // 적용 여부는 합집합 — 한쪽 문서라도 적용이면 적용 (수동 가감 반영)
        const anyApplicable = derivedList.some(d => d.items[idx].applicable);
        return { ...picked, key: def.key, label: def.label, applicable: anyApplicable };
    });
    const appItems = items.filter(i => i.applicable);
    const doneCount = appItems.filter(i => i.done).length;
    const next = appItems.find(i => !i.done) || null;
    const packingItem = items.find(i => i.key === 'packing');
    const base = derivedList.find(d => d.deadline) || derivedList[0];
    return {
        items, doneCount, total: appItems.length,
        progress: `${doneCount}/${appItems.length}`,
        nextStep: next ? next.key : null,
        nextLabel: next ? next.label : null,
        packed: !!(packingItem && packingItem.done),
        packedAt: packingItem && packingItem.done ? packingItem.at : null,
        deadline: base.deadline, deadlineBasis: base.deadlineBasis,
    };
}

/**
 * isHiddenAfterPacking(entity, days=4)
 * 포장 완료 + N일 지난 건을 "목록에서만" 감출지 여부. ★ 삭제가 아니다 —
 * 의뢰서·명세서·수집함에는 그대로 남아야 한다(기획서 §4-5).
 */
export function isHiddenAfterPacking(entity, days = 4) {
    const d = deriveChecks(entity);
    if (!d.packed || !d.packedAt) return false;
    return Date.now() - d.packedAt.getTime() > days * 86400000;
}

/*
사용 예시
--------------------------------------------------------------------
1) 자동 판정만 있는 수집 케이스
   deriveChecks({ processStage: 'milling', stageHistory: [{ stage:'scanning', at:'2026-08-08T09:12:00Z' }] })
   → items[0] { key:'scan', done:true, auto:true, at:Date(08-08 09:12) }
     items[1] { key:'mill', done:true, auto:true, at:null }   // 이력 없으면 시각만 비어 있고 done은 유지
     progress '2/6', nextStep 'implantMill'

2) 사람이 자동 항목을 되돌린 경우
   deriveChecks({ processStage:'milling', checks:{ mill:{ done:false, at:null, by:'user', revertedFromAuto:true } } })
   → items[1] { done:false, auto:false, reverted:true }       // 화면에 '되돌림' 배지

3) 포장 체크 저장용 패치 (workOrders)
   buildCheckPatch('packing', true, { processStage:'shipping' })
   → { 'checks.packing': { done:true, at:'2026-08-09T06:42:00.000Z', by:'user', revertedFromAuto:false },
       processStage:'shipping' }
   updateDoc(doc(db,'workOrders',id), patch) 로 그대로 사용.
--------------------------------------------------------------------
*/
